import { TrendingUp, DollarSign, Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import ReportFilters from "./ReportFilters";
import { buildPeriodRange } from "../../../utils/helpers";

const KPI = ({ title, value }) => (
  <div className="relative rounded-2xl border overflow-hidden border-[#F7BFAD] bg-white px-5 py-4">
    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
      {title}
    </p>

    <p className="text-2xl font-semibold text-gray-900 leading-tight">
      {value}
    </p>
  </div>
);

const SkeletonKPI = () => (
  <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4 shadow-sm animate-pulse">
    <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
    <div className="h-8 w-32 bg-gray-200 rounded" />
  </div>
);

const SkeletonContent = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-6 w-48 bg-gray-200 rounded" />
    <div className="space-y-2">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-12 bg-gray-100 rounded" />
      ))}
    </div>
  </div>
);

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-[#F7BFAD] rounded-lg p-3 shadow-lg">
        <p className="text-xs text-gray-500 mb-1">{payload[0].payload.date}</p>
        <p className="text-sm font-bold text-[#f0653a]">
          ₹{payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

const DEFAULT_FILTERS = {
  fromDate: "",
  toDate: "",
  paymentMode: "",
  reportType: "monthly",
};

const SalesReports = ({ branchId }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!filters.reportType) return;

    const { fromDate, toDate } = buildPeriodRange(filters.reportType);

    setFilters((prev) => ({
      ...prev,
      fromDate,
      toDate,
    }));
  }, [filters.reportType]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);

      const params = {
        sections: "summary,byService,byPayment,dailyTrend",
      };

      if (appliedFilters.fromDate) params.fromDate = appliedFilters.fromDate;
      if (appliedFilters.toDate) params.toDate = appliedFilters.toDate;
      if (appliedFilters.paymentMode)
        params.paymentMode = appliedFilters.paymentMode;
      if (branchId) {
        params.branchId = branchId;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/reports/sales`,
        { params },
      );

      const data = res.data;

      const transformed = {
        kpis: [
          {
            title: "Total Revenue",
            value: `₹${data.summary?.totalRevenue?.toLocaleString() || 0}`,
          },
          {
            title: "Avg Daily Revenue",
            value: `₹${data.summary?.avgDailyRevenue?.toLocaleString() || 0}`,
          },
          {
            title: "Top Service",
            value: data.summary?.topService?.name || "N/A",
            subtext: `₹${
              data.summary?.topService?.revenue?.toLocaleString() || 0
            }`,
          },
          {
            title: "UPI Share",
            value: `${data.summary?.upiShare || 0}%`,
          },
        ],
        revenueByService:
          data.byService?.slice(0, 10).map((item) => ({
            name: item._id,
            revenue: item.revenue,
            percentage: Math.round(
              (item.revenue / data.summary.totalRevenue) * 100,
            ),
          })) || [],
        revenueByPayment:
          data.byPayment?.map((item) => ({
            mode: item._id === "UPI_QR" ? "UPI" : item._id,
            amount: item.amount,
            count: item.count,
          })) || [],
        dailyRevenue:
          data.dailyTrend?.map((item) => ({
            date: new Date(item._id).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
            amount: item.amount,
          })) || [],
      };

      setSalesData(transformed);
    } catch (err) {
      console.error("Failed to fetch sales data", err);
      setSalesData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, [appliedFilters, branchId]);

  return (
    <div className="space-y-6">
      {/* FILTER BAR */}
      <ReportFilters
        filters={filters}
        appliedFilters={appliedFilters}
        setFilters={setFilters}
        showPaymentMode
        onApply={() => setAppliedFilters(filters)}
        onClearAll={() => {
          setFilters(DEFAULT_FILTERS);
          setAppliedFilters(DEFAULT_FILTERS);
        }}
        onRemoveFilter={(key) => {
          setFilters((p) => ({ ...p, [key]: "" }));
          setAppliedFilters((p) => ({ ...p, [key]: "" }));
        }}
        onExportPDF={() =>
          alert(`Exporting ${appliedFilters.reportType} Sales report as PDF`)
        }
        onExportExcel={() =>
          alert(`Exporting ${appliedFilters.reportType} Sales report as Excel`)
        }
      />

      {/* KPI CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
          : salesData?.kpis?.map((kpi, idx) => <KPI key={idx} {...kpi} />)}
      </div>

      {/* CONTENT */}
      <div className="bg-white border border-[#F7BFAD] rounded-2xl p-6 space-y-6">
        {loading ? (
          <SkeletonContent />
        ) : (
          <>
            {/* Revenue by Service */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <TrendingUp className="size-5 text-[#f0653a]" />
                Revenue by Service (Top 10)
              </h3>

              {salesData?.revenueByService?.length ? (
                <div className="space-y-2">
                  {salesData.revenueByService.map((service) => (
                    <div key={service.name} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">
                            {service.name}
                          </span>
                          <span className="text-sm text-gray-600">
                            ₹{service.revenue.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-[#f0653a] h-2 rounded-full"
                            style={{ width: `${service.percentage}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 w-12 text-right">
                        {service.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No data available</p>
              )}
            </div>

            {/* Payment Mode Breakdown */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <DollarSign className="size-5 text-[#f0653a]" />
                Payment Mode Breakdown
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {salesData?.revenueByPayment?.map((p) => (
                  <div
                    key={p.mode}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-800">
                        {p.mode}
                      </span>
                      <span className="text-lg font-bold text-gray-900">
                        ₹{p.amount.toLocaleString()}
                      </span>
                    </div>

                    <p className="mt-1 text-xs text-gray-500">
                      {p.count} transactions
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Daily Revenue Trend - UPDATED WITH RECHARTS */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Calendar className="size-5 text-[#f0653a]" />
                Daily Revenue Trend
              </h3>

              {salesData?.dailyRevenue?.length ? (
                <div className="bg-[#FFF7F3] rounded-xl p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={salesData.dailyRevenue}
                      margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#F7BFAD"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12, fill: "#6b7280" }}
                        axisLine={{ stroke: "#F7BFAD" }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#6b7280" }}
                        axisLine={{ stroke: "#F7BFAD" }}
                        tickLine={false}
                        tickFormatter={(value) => `₹${value.toLocaleString()}`}
                      />
                      <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "#FFF7F3" }}
                      />
                      <Bar
                        dataKey="amount"
                        fill="#f0653a"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={60}
                      >
                        {salesData.dailyRevenue.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index % 2 === 0 ? "#f0653a" : "#ff7f50"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 bg-[#FFF7F3] rounded-xl flex items-center justify-center">
                  <p className="text-sm text-gray-400">No data available</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SalesReports;
